import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Mail, Lock, ArrowLeft, Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import PasswordStrengthIndicator, { validatePassword } from "@/components/PasswordStrengthIndicator";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode?: "signin" | "signup";
  initialMode?: "signin" | "signup" | "forgot" | "new-password";
}

const AuthModal = ({ open, onOpenChange, defaultMode = "signin", initialMode }: AuthModalProps) => {
  const [mode, setMode] = useState<"signin" | "signup" | "forgot" | "new-password">(initialMode || defaultMode);
  const [activeTab, setActiveTab] = useState<string>(defaultMode === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signIn, signUp } = useAuth();

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode);
    }
  }, [initialMode]);

  useEffect(() => {
    if (!open) {
      if (mode !== "new-password") {
        setMode(defaultMode);
        setActiveTab(defaultMode === "signup" ? "signup" : "signin");
      }
      setNewPassword("");
      setConfirmNewPassword("");
    }
  }, [open, defaultMode, mode]);

  const handleSubmit = async (e: React.FormEvent, submitMode: "signin" | "signup") => {
    e.preventDefault();
    
    // Client-side password validation for signup
    if (submitMode === "signup") {
      const validationError = validatePassword(password);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setLoading(true);

    try {
      // Store return URL before BOTH sign-up and sign-in
      const currentUrl = window.location.pathname + window.location.search;
      sessionStorage.setItem("greyrock_return_url", currentUrl);
      // Persist in localStorage too so it survives email confirmation redirect
      localStorage.setItem("greyrock_return_url", currentUrl);

      if (submitMode === "signup") {
        const { error } = await signUp(email, password);
        if (error) {
          if (error.message.toLowerCase().includes("password")) {
            toast.error("Password doesn't meet requirements. Check the checklist below.");
          } else {
            toast.error(error.message);
          }
        } else {
          toast.success("Check your email to confirm your account!");
          onOpenChange(false);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          toast.error(error.message);
        } else {
          toast.success("Welcome back!");
          onOpenChange(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?type=recovery`,
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Check your email for a reset link!");
      onOpenChange(false);
    } catch {
      toast.error("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords don't match");
      return;
    }
    const validationError = validatePassword(newPassword);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        toast.error(error.message);
        return;
      }
      setNewPassword("");
      setConfirmNewPassword("");
      setMode("signin");
      onOpenChange(false);
      sessionStorage.removeItem("password_recovery_hash");
      sessionStorage.removeItem("password_recovery_pending");
      window.history.replaceState(null, "", window.location.origin + window.location.pathname);
      await supabase.auth.signOut();
      toast.success("Password updated successfully! Please sign in with your new password.");
    } catch {
      toast.error("Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === "forgot") return "Forgot Password";
    if (mode === "new-password") return "New Password";
    return "";
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    // Preserve current page so we can return after OAuth
    sessionStorage.setItem("greyrock_return_url", window.location.pathname + window.location.search);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
        extraParams: {
          prompt: "select_account",
        },
      });
      if (error) {
        toast.error("Google sign-in failed. Please try again.");
        console.error("Google OAuth error:", error);
      }
    } catch (err) {
      toast.error("Google sign-in failed. Please try again.");
      console.error("Google OAuth error:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderGoogleButton = () => (
    <div className="mb-4">
      <Button
        type="button"
        variant="outline"
        onClick={handleGoogleSignIn}
        disabled={loading}
        className="w-full py-3 rounded-xl border-glass-border bg-charcoal-medium text-foreground hover:bg-charcoal-medium/80 transition-colors"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </Button>
    </div>
  );

  const renderEmailPasswordForm = (formMode: "signin" | "signup") => (
    <form onSubmit={(e) => handleSubmit(e, formMode)} className="space-y-4">
      {renderGoogleButton()}

      <div className="relative flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-glass-border" />
        <span className="text-xs text-muted-foreground">or continue with email</span>
        <div className="flex-1 h-px bg-glass-border" />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${formMode}-email`} className="text-foreground">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id={`${formMode}-email`}
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="pl-10 bg-charcoal-medium border-glass-border text-foreground"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${formMode}-password`} className="text-foreground">Password</Label>
          {formMode === "signin" && (
            <button
              type="button"
              onClick={() => setMode("forgot")}
              className="text-xs text-cyan hover:underline"
            >
              Forgot password?
            </button>
          )}
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id={`${formMode}-password`}
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="pl-10 pr-10 bg-charcoal-medium border-glass-border text-foreground"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {formMode === "signup" && <PasswordStrengthIndicator password={password} />}
      </div>

      <Button
        type="submit"
        disabled={loading || (formMode === "signup" && validatePassword(password) !== null)}
        className={cn(
          "w-full py-3 rounded-xl font-bold",
          "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
          "hover:opacity-90 transition-opacity"
        )}
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : formMode === "signin" ? (
          "Sign In"
        ) : (
          "Create Account"
        )}
      </Button>

      {formMode === "signup" && (
        <p className="text-xs text-muted-foreground text-center">
          By signing up, you agree to our{" "}
          <a href="https://greyrockai.app/legal" target="_blank" rel="noopener noreferrer" className="text-cyan hover:underline">Terms of Service</a>
          {" "}and{" "}
          <a href="https://greyrockai.app/legal" target="_blank" rel="noopener noreferrer" className="text-cyan hover:underline">Privacy Policy</a>.
        </p>
      )}
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-charcoal-dark border-glass-border max-w-md">
        {mode === "new-password" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-foreground text-center">
                {getTitle()}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSetNewPassword} className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground text-center">
                Enter your new password below.
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-foreground">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 pr-10 bg-charcoal-medium border-glass-border text-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrengthIndicator password={newPassword} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password" className="text-foreground">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 pr-10 bg-charcoal-medium border-glass-border text-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className={cn(
                  "w-full py-3 rounded-xl font-bold",
                  "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                  "hover:opacity-90 transition-opacity"
                )}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}
              </Button>
            </form>
          </>
        ) : mode === "forgot" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-foreground text-center">
                {getTitle()}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-foreground">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 bg-charcoal-medium border-glass-border text-foreground"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className={cn(
                    "w-full py-3 rounded-xl font-bold",
                    "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                    "hover:opacity-90 transition-opacity"
                  )}
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setActiveTab("signin");
                  }}
                  className="w-full text-sm text-cyan hover:underline flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to Sign In
                </button>
              </form>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="pb-0">
              <DialogTitle className="sr-only">Authentication</DialogTitle>
            </DialogHeader>

            <Tabs
              value={activeTab}
              onValueChange={(val) => {
                setActiveTab(val);
                setMode(val as "signin" | "signup");
              }}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 bg-charcoal-medium/50 border border-glass-border">
                <TabsTrigger
                  value="signin"
                  className="data-[state=active]:bg-charcoal-dark data-[state=active]:text-cyan font-semibold"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  className="data-[state=active]:bg-charcoal-dark data-[state=active]:text-cyan font-semibold"
                >
                  Create Account
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="mt-6">
                {renderEmailPasswordForm("signin")}
              </TabsContent>

              <TabsContent value="signup" className="mt-6">
                {/* Free credits banner */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-cyan/10 border border-cyan/20 mb-5">
                  <Shield className="w-8 h-8 text-cyan shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Start with 3 free credits</p>
                    <p className="text-xs text-muted-foreground">No credit card required. Try the full experience.</p>
                  </div>
                </div>
                {renderEmailPasswordForm("signup")}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
